import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { SECTION_ITEMS, SectionItem, SidebarItem } from '../models/sideBarItem';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { PopoverModule } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { AuthAdminStore } from '@ribera-web/auth-data-acess';
import { filter } from 'rxjs';
import { ADMIN_APP_CONFIG } from '@ribera-web/app-token';

@Component({
  selector: 'lib-sidebar',
  imports: [DrawerModule, ButtonModule, CommonModule, RouterModule,PopoverModule, Tooltip],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar implements OnInit, OnDestroy {

  private authAdminStore = inject(AuthAdminStore);
  private router = inject(Router);
  private config = inject(ADMIN_APP_CONFIG);
  
  isCollapsed = false;
  mobileMenuVisible = false;

  private readonly mobileMenuToggleHandler = () => {
    this.mobileMenuVisible = !this.mobileMenuVisible;
  };

  public sectionItems = signal<SectionItem[]>(SECTION_ITEMS);

  public filteredMenu = computed(() => {
    // 1. IMPORTANTE: Debes "leer" el signal de permisos aquí para que sea reactivo
    const permissions = this.authAdminStore.permissions(); 
    
    // 2. Si aún no hay permisos y no quieres que el menú desaparezca,
    // puedes retornar un arreglo vacío o los items públicos únicamente.
    console.log('Filtrando menú con permisos:', permissions);

    return this.sectionItems()
      .map(section => ({
        ...section,
        items: this.filterSidebarItems(section.items)
      }))
      .filter(section => section.items.length > 0);
  });

  private filterSidebarItems(items: SidebarItem[]): SidebarItem[] {
    return items
      .map(item => {
        if (!this.isFeatureItemEnabled(item)) {
          return null;
        }

        const newItem = { ...item };
        
        // Si tiene hijos, los filtramos primero recursivamente
        if (newItem.children) {
          newItem.children = this.filterSidebarItems(newItem.children);
        }

        // Lógica de visibilidad:
        // 1. Si tiene código, checamos permiso.
        // 2. Si es un padre (no tiene route pero tiene hijos), vemos si le quedaron hijos visibles.
        const hasDirectPermission = !item.code || this.authAdminStore.hasPermission(item.code+'_VIEW') || this.authAdminStore.hasPermission(item.code+'_EDIT');
        const hasVisibleChildren = newItem.children && newItem.children.length > 0;
        
        if (item.route) {
            return hasDirectPermission ? newItem : null;
        } else {
            return hasVisibleChildren ? newItem : null;
        }
      })
      .filter(item => item !== null) as SidebarItem[];
  }

  private isFeatureItemEnabled(item: SidebarItem): boolean {
    if (item.id === 'gestor-de-pagos-parciales') {
      return this.config.features?.partialPayments ?? true;
    }

    if (item.id === 'gestor-de-pagos') {
      return this.config.features?.newPaymentManager ?? true;
    }

    return true;
  }

  constructor() {
    window.addEventListener('admin-mobile-menu-toggle', this.mobileMenuToggleHandler);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.mobileMenuVisible = false;
        this.expandByRoute(event.urlAfterRedirects);
      });
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('sidebarCollapsed', JSON.stringify(this.isCollapsed));
  }

  ngOnInit(): void {
    this.loadState();
  }

  ngOnDestroy(): void {
    window.removeEventListener('admin-mobile-menu-toggle', this.mobileMenuToggleHandler);
  }

  toggleSubmenu(id: string) {
  this.filteredMenu().forEach(section => {
      section.items.forEach(item => {
        if (item.id === id) {
          item.expanded = !item.expanded;
          const storageKey = {'id': id, 'expanded': item.expanded};
          localStorage.setItem('sidebarState', JSON.stringify(storageKey));
        } else {
          item.expanded = false;
        }
      });
    });
  }

  loadState(){
    const storedState = localStorage.getItem('sidebarState');
    if (storedState) {
      const { id, expanded } = JSON.parse(storedState);
      this.filteredMenu().forEach(section => {
        section.items.forEach(item => {
          if (item.id === id) {
            item.expanded = expanded;
          }
        });
      });
    }

    const storedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (storedCollapsed) {
      this.isCollapsed = JSON.parse(storedCollapsed);
    }
  }

  onLogout() {
    this.authAdminStore.logout();
  }

  closeMobileMenu() {
    this.mobileMenuVisible = false;
  }

  onMobileSidebarItemClick(event: Event, item: SidebarItem) {
    if (item.children?.length) {
      event.preventDefault();
      this.toggleSubmenu(item.id);
      return;
    }

    this.closeMobileMenu();
  }

  expandByRoute(id: string) {
    this.filteredMenu().forEach(section => {
    section.items.forEach(item => {

      const match = item.children?.some(child =>
        id.startsWith(child.route? child.route : '')
      );

      if (match) {
        item.expanded = true;

        localStorage.setItem(
          'sidebarState',
          JSON.stringify({ id: item.id, expanded: true })
        );
      } else {
        item.expanded = false;
      }

    });
  });
  }

}
